/**
 * #699 -- drag and drop, the pure part (the React provider and hooks are in DragDrop.tsx).
 *
 * Built on pointer events rather than the browser's native drag-and-drop, which does not work on
 * touch screens: a mouse or pen drag starts once the pointer has moved a little (so clicks still
 * work); a touch drag starts after a short press-and-hold (so a swipe still scrolls the results).
 */

/** What a drag carries. `kind` is what drop targets match on; the rest is the source's data. */
export interface DragPayload {
  readonly kind: string;
  /** Spoken while dragging ("Dragging octane"). */
  readonly label: string;
  readonly [key: string]: unknown;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface TargetRect {
  readonly id: string;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** Movement (px) before a mouse / pen press becomes a drag. */
export const DRAG_START_DISTANCE = 6;
/** Hold (ms) before a touch press becomes a drag; moving farther than TOUCH_SLOP first scrolls. */
export const TOUCH_HOLD_MS = 350;
export const TOUCH_SLOP = 8;
/** Distance (px) from a scroll container's edge at which a drag scrolls it. */
export const AUTOSCROLL_EDGE = 48;

/** The drop target under a point: among those containing it, the innermost (smallest) one, so a
 *  target nested inside another (a box on the paper) wins over its container. */
export function pickTarget(targets: readonly TargetRect[], point: Point): string | null {
  let best: TargetRect | null = null;
  let bestArea = Infinity;
  for (const target of targets) {
    if (point.x < target.left || point.x > target.right || point.y < target.top || point.y > target.bottom) continue;
    const area = (target.right - target.left) * (target.bottom - target.top);
    if (area < bestArea) {
      best = target;
      bestArea = area;
    }
  }
  return best?.id ?? null;
}

/** Whether a mouse / pen press has moved far enough to start dragging. */
export function passedDragThreshold(start: Point, current: Point): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) >= DRAG_START_DISTANCE;
}

/** Whether a touch press moved too far before the hold completed (it is a scroll, not a drag). */
export function exceededTouchSlop(start: Point, current: Point): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) > TOUCH_SLOP;
}

/** How fast (px per frame, signed) to scroll a container when the pointer is near its top or
 *  bottom edge; 0 away from the edges. Faster the closer it gets. */
export function autoScrollSpeed(rect: { readonly top: number; readonly bottom: number }, y: number): number {
  if (y < rect.top + AUTOSCROLL_EDGE) return -Math.ceil(((rect.top + AUTOSCROLL_EDGE - y) / AUTOSCROLL_EDGE) * 18);
  if (y > rect.bottom - AUTOSCROLL_EDGE)
    return Math.ceil(((y - (rect.bottom - AUTOSCROLL_EDGE)) / AUTOSCROLL_EDGE) * 18);
  return 0;
}

/** Presses that start on these stay theirs: a card's own buttons and menus never start a drag. */
const INTERACTIVE = 'button, a, input, select, textarea, [role="menu"], [role="menuitem"]';

/** Whether a press on `target` belongs to a control inside `source` (so it must not drag it).
 *  A control that is itself the source (a draggable menu item) does drag, and so does one marked
 *  `data-drags-card` (#963: a card's picture -- a click enlarges it, a drag still drags the card). */
export function startsOnControl(target: EventTarget | null, source?: Element): boolean {
  if (!(target instanceof Element)) return false;
  const control = target.closest(INTERACTIVE);
  if (control?.hasAttribute('data-drags-card')) return false;
  return control !== null && control !== source && (!source || source.contains(control));
}
